import { useAnalytics, getCategoryMeta } from '../hooks/useAnalytics';
import { useAuth } from '../hooks/useAuth';
import { useIncomes } from '../hooks/useIncomes';
import { useState } from 'react';
import toast from 'react-hot-toast';
import SpendingChart from '../components/dashboard/SpendingChart';
import CategoryBreakdown from '../components/dashboard/CategoryBreakdown';
import StatsCard from '../components/dashboard/StatsCard';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import Select from '../components/ui/Select';
import { Euro, TrendingUp, RefreshCw, Tag, BarChart3, Lock, Trash2, PieChart, Wallet, Sparkles, Upload, FileDown } from 'lucide-react';
import { RECURRING_TYPE_META, RECURRING_TYPES } from '../types';
import { isPremiumPlan } from '../lib/plans';
import type { ParsedPayslip } from '../types';
import { jsPDF } from 'jspdf';
import { supabase } from '../lib/supabase';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  Sankey,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  Legend,
} from 'recharts';

// ─── Tooltip ─────────────────────────────────────────────────────────────────

function BarTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ value: number; payload: { count: number } }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card px-3 py-2 shadow-xl text-sm border border-slate-700/60">
      <p className="text-slate-400 mb-1 text-xs">{label}</p>
      <p className="text-white font-semibold">€{Number(payload[0].value).toFixed(2)}</p>
      <p className="text-slate-500 text-xs">{payload[0].payload.count} transazioni</p>
    </div>
  );
}

function SavingsTooltip({ active, payload, label }: {
  active?: boolean;
  payload?: Array<{ value: number; name: string; color: string }>;
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  return (
    <div className="card px-3 py-2 shadow-xl text-sm border border-slate-700/60">
      <p className="text-slate-400 mb-2 text-xs">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2 mb-1">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-slate-300 text-xs">{p.name}:</span>
          <span className="text-white text-xs font-medium">€{Number(p.value).toFixed(2)}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Premium lock card ────────────────────────────────────────────────────────

function PremiumLock({ label }: { label: string }) {
  return (
    <div className="card p-10 flex flex-col items-center justify-center text-center border border-yellow-500/20 bg-yellow-500/5">
      <Lock size={28} className="text-yellow-400 mb-3" />
      <p className="text-slate-200 font-semibold text-sm">{label}</p>
      <p className="text-slate-500 text-xs mt-1">Disponibile con il piano Premium.</p>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

type AnalyticsTab = 'panoramica' | 'spese' | 'entrate' | 'visuals';

export default function Analytics() {
  const { profile, user, session } = useAuth();
  const { analytics, loading, error, refetch } = useAnalytics();
  const { incomes, addIncome, deleteIncome, disableRecurringIncome, loading: incomesLoading } = useIncomes();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('panoramica');
  const [incomeSource, setIncomeSource] = useState('');
  const [incomeAmount, setIncomeAmount] = useState('');
  const [incomeDate, setIncomeDate] = useState(new Date().toISOString().slice(0, 10));
  const [incomeRecurring, setIncomeRecurring] = useState(false);
  const [incomeRecurringType, setIncomeRecurringType] = useState<'weekly' | 'monthly' | 'yearly'>('monthly');
  const [parsingPayslip, setParsingPayslip] = useState(false);
  const [importingPayslip, setImportingPayslip] = useState(false);
  const [payslip, setPayslip] = useState<ParsedPayslip | null>(null);
  const [payslipFileName, setPayslipFileName] = useState('');
  const isPremium = isPremiumPlan(profile?.plan);

  // ── Loaders / errors ──────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-8 text-center">
        <p className="text-red-400 text-sm">{error}</p>
      </div>
    );
  }

  if (!isPremium) {
    return <PremiumLock label="Analytics disponibili solo con il piano Premium" />;
  }

  if (!analytics || analytics.expenseCount === 0) {
    return (
      <div className="card p-16 text-center">
        <BarChart3 size={40} className="text-slate-600 mx-auto mb-4" />
        <p className="text-slate-300 font-medium">Nessun dato</p>
        <p className="text-slate-500 text-sm mt-1">Aggiungi delle spese per vedere le analisi.</p>
      </div>
    );
  }

  // ── Derived data ──────────────────────────────────────────────────────────
  const hasVisuals = isPremium && !!analytics.premiumVisuals;

  const barData = analytics.byCategory.map(s => ({
    name: `${getCategoryMeta(s.category).icon} ${getCategoryMeta(s.category).label}`,
    total: s.total,
    count: s.count,
    fill: getCategoryMeta(s.category).color,
  }));

  // ── Income handlers ───────────────────────────────────────────────────────

  const handleIncomeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const amount = Number(incomeAmount);
    if (!incomeSource.trim() || !amount || amount <= 0) return;
    await addIncome({
      source: incomeSource.trim(),
      amount,
      date: incomeDate,
      recurring: incomeRecurring,
      recurring_type: incomeRecurring ? incomeRecurringType : 'none',
    });
    setIncomeSource('');
    setIncomeAmount('');
    setIncomeRecurring(false);
    setIncomeRecurringType('monthly');
    await refetch();
  };

  const handleDeleteIncome = async (id: string) => {
    await deleteIncome(id);
    await refetch();
  };

  const handleDisableRecurringIncome = async (id: string) => {
    await disableRecurringIncome(id);
    toast.success('Entrata ricorrente disabilitata');
    await refetch();
  };

  const extractPdfText = async (file: File): Promise<string> => {
    const pdfjsLib = await import('pdfjs-dist');
    pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();

    const bytes = new Uint8Array(await file.arrayBuffer());
    const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
    let text = '';

    for (let i = 1; i <= pdf.numPages; i += 1) {
      const page = await pdf.getPage(i);
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item: any) => ('str' in item ? item.str : ''))
        .join(' ');
      text += `\n${pageText}`;
    }

    return text;
  };

  const handlePayslipUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Carica un file PDF');
      return;
    }
    if (!isPremium) {
      toast.error('Funzione disponibile solo per Premium');
      return;
    }

    setParsingPayslip(true);
    setPayslipFileName(file.name);
    try {
      const text = await extractPdfText(file);
      const { data, error: parseError } = await supabase.functions.invoke('parse-payslip', {
        body: { text },
        headers: session?.access_token
          ? { Authorization: `Bearer ${session.access_token}` }
          : undefined,
      });
      if (parseError) throw parseError;
      setPayslip(data as ParsedPayslip);
      toast.success('Busta paga analizzata');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Analisi PDF fallita');
      setPayslip(null);
    } finally {
      setParsingPayslip(false);
      e.target.value = '';
    }
  };

  const importPayslipData = async () => {
    if (!user || !payslip) return;
    setImportingPayslip(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const incomeEntries = payslip.entries.filter((x) => x.kind === 'income');
      const expenseEntries = payslip.entries.filter((x) => x.kind === 'expense');

      if (incomeEntries.length > 0) {
        const payload = incomeEntries.map((entry) => ({
          user_id: user.id,
          source: entry.description,
          amount: entry.amount,
          date: today,
          recurring: false,
          recurring_type: 'none',
        }));
        const { error: incomeInsertError } = await supabase.from('incomes').insert(payload);
        if (incomeInsertError) throw incomeInsertError;
      } else if (payslip.netIncome > 0) {
        const { error: incomeFallbackError } = await supabase.from('incomes').insert({
          user_id: user.id,
          source: 'Stipendio netto (PDF)',
          amount: payslip.netIncome,
          date: today,
          recurring: false,
          recurring_type: 'none',
        });
        if (incomeFallbackError) throw incomeFallbackError;
      }

      if (expenseEntries.length > 0) {
        const payload = expenseEntries.map((entry) => ({
          user_id: user.id,
          description: entry.description,
          amount: entry.amount,
          category: entry.category || 'other',
          date: today,
          recurring: false,
          recurring_type: 'none',
          is_template: false,
        }));
        const { error: expenseInsertError } = await supabase.from('expenses').insert(payload);
        if (expenseInsertError) throw expenseInsertError;
      }

      toast.success('Dati busta paga importati in entrate/spese');
      await refetch();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Import busta paga fallito');
    } finally {
      setImportingPayslip(false);
    }
  };

  const exportPayslipReport = () => {
    if (!payslip) return;
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text('BudgetAI - Report Busta Paga', 14, 18);
    doc.setFontSize(11);
    doc.text(`File: ${payslipFileName || 'N/A'}`, 14, 28);
    doc.text(`Periodo: ${payslip.period ?? 'N/A'}`, 14, 35);
    doc.text(`Confidenza AI: ${(payslip.confidence * 100).toFixed(0)}%`, 14, 42);

    doc.text(`Lordo: EUR ${payslip.grossIncome.toFixed(2)}`, 14, 52);
    doc.text(`Netto: EUR ${payslip.netIncome.toFixed(2)}`, 14, 59);
    doc.text(`IRPEF: EUR ${payslip.taxes.irpef.toFixed(2)}`, 14, 66);
    doc.text(`INPS: EUR ${payslip.taxes.inps.toFixed(2)}`, 14, 73);
    doc.text(`Addizionali: EUR ${payslip.taxes.addizionali.toFixed(2)}`, 14, 80);
    doc.text(`Altre trattenute: EUR ${payslip.taxes.other.toFixed(2)}`, 14, 87);

    let y = 98;
    doc.text('Voci estratte:', 14, y);
    y += 7;

    payslip.entries.slice(0, 18).forEach((entry, i) => {
      doc.text(`${i + 1}. [${entry.kind}] ${entry.description} - EUR ${entry.amount.toFixed(2)}`, 14, y);
      y += 6;
    });

    doc.save(`budgetai-busta-paga-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  const totalTax = payslip
    ? payslip.taxes.irpef + payslip.taxes.inps + payslip.taxes.addizionali + payslip.taxes.other
    : 0;
  const effectiveTaxRate = payslip && payslip.grossIncome > 0
    ? (totalTax / payslip.grossIncome) * 100
    : 0;

  // ── Sankey renderers ──────────────────────────────────────────────────────

  // Build a set of known category names so we can distinguish them from income sources
  const knownCategoryNames = new Set(analytics.byCategory.map((c) => c.category));

  const renderSankeyNode = (props: any) => {
    const { x, y, width, height, payload } = props;
    const rawName: string = payload?.name ?? '';
    const value = (payload?.value ?? 0) as number;
    const displayValue = `€${value.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

    const isHub      = rawName === 'Entrate Totali';
    const isSavings  = rawName === 'Risparmi (Netto)';
    const isPrevSav  = rawName === 'Risparmi';
    const isCategory = knownCategoryNames.has(rawName);
    // everything else is a user-defined income source (left side)
    const isIncomeSource = !isHub && !isSavings && !isPrevSav && !isCategory;

    const isLeftNode = isIncomeSource || isPrevSav;

    const color = isHub      ? '#6366f1' // hub
      : isSavings  ? '#14b8a6' // teal
      : isPrevSav  ? '#3b82f6' // blue
      : isCategory ? getCategoryMeta(rawName).color
      : '#22c55e'; // default green for incomes

    const baseLabel = isHub      ? '💰 Entrate Totali'
      : isSavings  ? '🏦 Risparmi Netto'
      : isCategory ? `${getCategoryMeta(rawName).icon} ${getCategoryMeta(rawName).label}`
      : rawName;

    const fullLabel = `${baseLabel}  ${displayValue}`;

    // Hub: label above the node so it's not constrained by the node width
    if (isHub) {
      return (
        <g>
          <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.9}
            stroke="#1e293b" strokeWidth={1} rx={3} ry={3} />
          <text x={x + width / 2} y={y - 12} fill="#ffffff" fontSize={13}
            dominantBaseline="auto" textAnchor="middle" fontWeight={700}>
            {fullLabel}
          </text>
        </g>
      );
    }

    const labelX = isLeftNode ? x - 8 : x + width + 8;
    const anchor = isLeftNode ? 'end' : 'start';
    const textColor = isSavings ? '#5eead4' : '#cbd5e1';

    return (
      <g>
        <rect x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.88}
          stroke="#0f172a" strokeWidth={1} rx={3} ry={3} />
        <text x={labelX} y={y + height / 2} fill={textColor} fontSize={12}
          dominantBaseline="middle" textAnchor={anchor} fontWeight={isSavings ? 600 : 400}>
          {fullLabel}
        </text>
      </g>
    );
  };

  const renderSankeyLink = (props: any) => {
    const { sourceX, targetX, sourceY, targetY, sourceControlX, targetControlX, linkWidth, payload } = props;
    const categoryName: string = payload?.category ?? '';
    const color = categoryName === 'savings' ? '#14b8a6'
      : categoryName ? getCategoryMeta(categoryName).color
      : '#22c55e';
    const path = `M${sourceX},${sourceY}C${sourceControlX ?? sourceX},${sourceY} ${targetControlX ?? targetX},${targetY} ${targetX},${targetY}`;
    return <path d={path} stroke={color} strokeOpacity={0.4} strokeWidth={Math.max(linkWidth ?? 1, 1)} fill="none" />;
  };

  // ── Tab bar ───────────────────────────────────────────────────────────────

  const tabs: { key: AnalyticsTab; label: string; icon: React.ReactNode }[] = [
    { key: 'panoramica', label: 'Panoramica', icon: <BarChart3 size={14} /> },
    { key: 'spese',      label: 'Spese',      icon: <PieChart size={14} /> },
    { key: 'entrate',    label: 'Entrate',    icon: <Wallet size={14} /> },
    { key: 'visuals',    label: 'Avanzate',    icon: <Sparkles size={14} /> },
  ];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Analytics</h1>
          <p className="text-slate-400 text-sm mt-0.5">Ultimi 12 mesi</p>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 mb-6 bg-slate-900/50 border border-slate-800 rounded-xl p-1 w-fit">
        {tabs.map(({ key, label, icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={[
              'flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg transition-all duration-150',
              activeTab === key
                ? 'bg-brand-600/25 border border-brand-500/40 text-brand-300 shadow-sm'
                : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/60',
            ].join(' ')}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {/* ── PANORAMICA ───────────────────────────────────────────────────────── */}
      {activeTab === 'panoramica' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              title="Speso (12 mesi)"
              value={`€${analytics.totalSpent.toFixed(2)}`}
              icon={<Euro size={18} className="text-brand-400" />}
            />
            <StatsCard
              title="Questo mese"
              value={`€${analytics.currentMonthSpent.toFixed(2)}`}
              icon={<TrendingUp size={18} className="text-violet-400" />}
              iconBg="bg-violet-500/15"
              trend={analytics.monthlyChange}
              trendLabel="vs mese scorso"
            />
            <StatsCard
              title="Ricorrenti"
              value={`€${analytics.recurringTotal.toFixed(2)}`}
              icon={<RefreshCw size={18} className="text-blue-400" />}
              iconBg="bg-blue-500/15"
            />
            <StatsCard
              title="Categoria top"
              value={analytics.topCategory ? getCategoryMeta(analytics.topCategory).label : '—'}
              icon={<Tag size={18} className="text-emerald-400" />}
              iconBg="bg-emerald-500/15"
            />
          </div>

          {/* Mini spending chart preview */}
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Andamento mensile</h3>
            <SpendingChart data={analytics.byMonth} />
          </div>
        </div>
      )}

      {/* ── SPESE ────────────────────────────────────────────────────────────── */}
      {activeTab === 'spese' && (
        <div className="space-y-6">
          {/* Monthly spending */}
          <SpendingChart data={analytics.byMonth} />

          {/* Category breakdown + bar chart side by side on large screens */}
          <div className="grid lg:grid-cols-2 gap-6">
            <CategoryBreakdown data={analytics.byCategory} totalSpent={analytics.totalSpent} />

            <div className="card p-5">
              <h3 className="text-sm font-semibold text-slate-300 mb-5">Spesa per categoria</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11, fill: '#64748b' }}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={v => `€${v}`}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#94a3b8' }}
                    axisLine={false}
                    tickLine={false}
                    width={130}
                  />
                  <Tooltip content={<BarTooltip />} />
                  <Bar dataKey="total" radius={[0, 6, 6, 0]}>
                    {barData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* ── ENTRATE ──────────────────────────────────────────────────────────── */}
      {activeTab === 'entrate' && (
        <div className="space-y-6">
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-slate-300 mb-4">Aggiungi entrata</h3>
            <form onSubmit={handleIncomeSubmit} className="space-y-4">
              <div className="grid md:grid-cols-3 gap-3">
                <Input
                  label="Fonte"
                  placeholder="Stipendio, Freelance, Affitto..."
                  value={incomeSource}
                  onChange={(e) => setIncomeSource(e.target.value)}
                />
                <Input
                  label="Importo (€)"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={incomeAmount}
                  onChange={(e) => setIncomeAmount(e.target.value)}
                />
                <Input
                  label="Data"
                  type="date"
                  value={incomeDate}
                  onChange={(e) => setIncomeDate(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={incomeRecurring}
                    onChange={(e) => setIncomeRecurring(e.target.checked)}
                    className="h-4 w-4 rounded"
                  />
                  <span className="text-sm text-slate-300">Entrata ricorrente</span>
                </label>
                {incomeRecurring && (
                  <div className="w-40">
                    <Select
                      label=""
                      value={incomeRecurringType}
                      onChange={(e) => setIncomeRecurringType(e.target.value as 'weekly' | 'monthly' | 'yearly')}
                      options={RECURRING_TYPES.filter(t => t !== 'none').map(t => ({ value: t, label: RECURRING_TYPE_META[t].label }))}
                    />
                  </div>
                )}
                <div className="ml-auto">
                  <Button type="submit" loading={incomesLoading}>
                    Aggiungi entrata
                  </Button>
                </div>
              </div>
            </form>
          </div>

          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Upload size={16} className="text-cyan-400" />
              <h3 className="text-sm font-semibold text-slate-300">Upload busta paga (AI)</h3>
            </div>

            {!isPremium && (
              <p className="text-xs text-amber-300 mb-3">
                Funzione Premium: passa al piano Premium per analizzare PDF e importare entrate/spese automaticamente.
              </p>
            )}

            <div>
              <input
                type="file"
                accept="application/pdf"
                onChange={handlePayslipUpload}
                disabled={!isPremium || parsingPayslip}
                className="block w-full text-sm text-slate-300 disabled:opacity-50 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-slate-100"
              />
              {parsingPayslip && <p className="text-sm text-brand-300 mt-2">Analisi AI in corso...</p>}
            </div>

            {payslip && (
              <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 space-y-3 mt-4">
                <p className="text-sm text-slate-200 font-medium">
                  Risultato: {payslip.period ?? 'Periodo non rilevato'} · Confidenza {(payslip.confidence * 100).toFixed(0)}%
                </p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <p className="text-slate-300">Lordo: <span className="text-white font-medium">€{payslip.grossIncome.toFixed(2)}</span></p>
                  <p className="text-slate-300">Netto: <span className="text-white font-medium">€{payslip.netIncome.toFixed(2)}</span></p>
                  <p className="text-slate-300">Tasse totali: <span className="text-white font-medium">€{totalTax.toFixed(2)}</span></p>
                  <p className="text-slate-300">Aliquota effettiva: <span className="text-white font-medium">{effectiveTaxRate.toFixed(1)}%</span></p>
                </div>

                <div className="space-y-1">
                  {payslip.entries.slice(0, 6).map((entry, idx) => (
                    <p key={`${entry.description}-${idx}`} className="text-xs text-slate-400">
                      [{entry.kind}] {entry.description} - €{entry.amount.toFixed(2)}
                    </p>
                  ))}
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  <Button onClick={importPayslipData} loading={importingPayslip}>
                    Importa in entrate/spese
                  </Button>
                  <Button variant="secondary" onClick={exportPayslipReport} leftIcon={<FileDown size={14} />}>
                    Esporta report PDF
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="card p-5">
            <h3 className="text-sm font-semibold text-slate-300 mb-4">
              Storico entrate
              <span className="ml-2 text-xs text-slate-500 font-normal">({incomes.length} totali)</span>
            </h3>
            {incomes.length === 0 ? (
              <p className="text-slate-500 text-sm py-4 text-center">Nessuna entrata inserita.</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-auto pr-1">
                {incomes.map((income) => (
                  <div
                    key={income.id}
                    className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/40 px-4 py-3"
                  >
                    <div>
                      <p className="text-sm text-slate-200 font-medium">{income.source}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {income.date}
                        {income.recurring && (
                          <span className="ml-2 text-yellow-400/80">
                            {RECURRING_TYPE_META[income.recurring_type]?.icon}{' '}
                            {RECURRING_TYPE_META[income.recurring_type]?.label}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <p className="text-sm text-emerald-300 font-semibold">
                        +€{Number(income.amount).toFixed(2)}
                      </p>
                      <button
                        className="text-slate-500 hover:text-amber-300 transition-colors"
                        onClick={() => handleDisableRecurringIncome(income.id)}
                        type="button"
                        title="Disabilita ricorrenza"
                        disabled={!income.recurring}
                      >
                        <RefreshCw size={14} />
                      </button>
                      <button
                        className="text-slate-600 hover:text-red-400 transition-colors"
                        onClick={() => handleDeleteIncome(income.id)}
                        type="button"
                        title="Rimuovi"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── VISUALS PREMIUM ───────────────────────────────────────────────────── */}
      {activeTab === 'visuals' && (
        hasVisuals ? (
          <div className="space-y-6">
            {/* Sankey */}
            <div className="card p-5 overflow-x-auto">
              <h3 className="text-sm font-semibold text-slate-300 mb-1">Sankey</h3>
              <p className="text-xs text-slate-500 mb-4">Distribuzione proporzionale delle entrate sulle categorie di spesa</p>
              <div className="min-w-[600px]">
                <ResponsiveContainer width="100%" height={400}>
                  <Sankey
                    data={analytics.premiumVisuals!.sankey}
                    nodePadding={40}
                    node={renderSankeyNode}
                    link={renderSankeyLink}
                    margin={{ top: 32, right: 140, bottom: 8, left: 140 }}
                  />
                </ResponsiveContainer>
              </div>
            </div>

            {/* Savings line */}
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-slate-300 mb-1">Trend Risparmio Reale</h3>
              <p className="text-xs text-slate-500 mb-4">Entrate, spese e risparmio netto mese per mese</p>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={analytics.premiumVisuals!.savingsLine}
                  margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}`} />
                  <Tooltip content={<SavingsTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: 12, paddingTop: 12 }}
                    formatter={(value) => <span className="text-slate-300">{value}</span>}
                  />
                  <Line type="monotone" dataKey="income" stroke="#22c55e" strokeWidth={2} dot={false} name="Entrate" />
                  <Line type="monotone" dataKey="spent"  stroke="#f97316" strokeWidth={2} dot={false} name="Spese" />
                  <Line type="monotone" dataKey="saving" stroke="#38bdf8" strokeWidth={2.5} dot={{ r: 3, fill: '#38bdf8' }} name="Risparmio" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        ) : (
          <PremiumLock label="Visualizzazioni avanzate disponibili con il piano Premium" />
        )
      )}
    </>
  );
}
